import React, { useState, useEffect, useRef, useId } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useDisplayCurrency } from '../../hooks/useDisplayCurrency';
import { DISPLAY_CURRENCIES, setDisplayCurrency } from '../../utils/currency';
import './UserMenu.css';

const UserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const currency = useDisplayCurrency();
  const currencyId = useId();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  if (!user) {
    return null;
  }

  const getInitials = () => {
    return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <div className="user-menu" ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="user-menu-button"
        aria-label="Account menu"
        aria-expanded={isOpen}
        onClick={toggleDropdown}
        style={{
          background: '#aa8453',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          color: '#fff',
          fontWeight: 'bold',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {user.avatar ? (
          <img
            src={user.avatar}
            alt={`${user.firstName} ${user.lastName}`}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              objectFit: 'cover'
            }}
          />
        ) : (
          getInitials()
        )}
      </button>

      {isOpen && (
        <div
          className="user-menu-dropdown"
          style={{
            position: 'absolute',
            top: '50px',
            right: '0',
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            minWidth: '220px',
            zIndex: 1000
          }}
        >
          <div
            style={{
              padding: '15px',
              borderBottom: '1px solid #eee'
            }}
          >
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
              {user.firstName} {user.lastName}
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              {user.email}
            </div>
          </div>

          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            <li>
              <Link to="/subscription" onClick={() => setIsOpen(false)}
                style={{ display: 'block', padding: '12px 15px', color: '#333', textDecoration: 'none' }}>
                <i className="ti-id-badge" aria-hidden="true" style={{ marginRight: '10px' }}></i>
                My Membership
              </Link>
            </li>
            <li>
              <Link
                to="/my-bookings"
                onClick={() => setIsOpen(false)}
                style={{
                  display: 'block',
                  padding: '12px 15px',
                  color: '#333',
                  textDecoration: 'none',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <i className="ti-calendar" style={{ marginRight: '10px' }}></i>
                My Bookings
              </Link>
            </li>
            <li>
              <Link
                to="/loyalty-cards"
                onClick={() => setIsOpen(false)}
                style={{
                  display: 'block',
                  padding: '12px 15px',
                  color: '#333',
                  textDecoration: 'none',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <i className="ti-id-badge" style={{ marginRight: '10px' }}></i>
                Hotel loyalty cards
              </Link>
            </li>
            <li>
              <Link
                to="/favourites"
                onClick={() => setIsOpen(false)}
                style={{
                  display: 'block',
                  padding: '12px 15px',
                  color: '#333',
                  textDecoration: 'none',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <i className="ti-heart" style={{ marginRight: '10px' }}></i>
                My Favourites
              </Link>
            </li>
            <li>
              <Link
                to="/settings"
                onClick={() => setIsOpen(false)}
                style={{
                  display: 'block',
                  padding: '12px 15px',
                  color: '#333',
                  textDecoration: 'none',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <i className="ti-settings" style={{ marginRight: '10px' }}></i>
                Settings
              </Link>
            </li>
            <li className="user-menu-currency">
              <label htmlFor={currencyId}>
                <i className="ti-world" aria-hidden="true"></i>
                Currency
              </label>
              <select id={currencyId} value={currency} onChange={(event) => setDisplayCurrency(event.target.value)} aria-describedby={`${currencyId}-help`}>
                {DISPLAY_CURRENCIES.map(({ code, name }) => (
                  <option key={code} value={code}>{code} — {name}</option>
                ))}
              </select>
              <p id={`${currencyId}-help`}>For hotel prices. Hotels may charge in their local currency.</p>
            </li>
          </ul>

          <div
            style={{
              borderTop: '1px solid #eee',
              padding: '10px 15px'
            }}
          >
            <button
              onClick={handleLogout}
              style={{
                width: '100%',
                padding: '10px',
                background: '#dc3545',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              <i className="ti-power-off" style={{ marginRight: '10px' }}></i>
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
